import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus } from "lucide-react";
import { api, fmtDate } from "../lib/api";
import { fmtDay, daysUntil } from "../lib/dates";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";

export default function B2BStudents() {
  const [rows, setRows] = useState(null);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const nav = useNavigate();

  const load = async () => { const r = await api.get("/b2b/students", { params: q ? { q } : {} }); setRows(r.data); };
  useEffect(() => { load(); }, [q]); // eslint-disable-line

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="heading-font text-3xl font-bold text-slate-900">Students</h1>
          <p data-testid="b2b-students-count" className="text-sm text-slate-500 mt-1">{rows === null ? "Loading…" : `${rows.length} student${rows.length !== 1 ? "s" : ""} · ${rows.reduce((n, s) => n + s.app_counts.total, 0)} application(s)`}</p>
        </div>
        <Button data-testid="b2b-students-add-btn" onClick={() => setOpen(true)} className="bg-orange-500 hover:bg-orange-600 text-white">
          <Plus className="w-4 h-4 mr-2" /> New Student
        </Button>
      </div>
      <input data-testid="b2b-students-search" placeholder="Search name, email, phone, ID…" value={q} onChange={e => setQ(e.target.value)}
             className="h-9 px-3 rounded-md border border-slate-200 text-sm bg-white w-72" />
      <div className="bg-white rounded-xl border border-slate-200 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs uppercase text-slate-500">
            <tr>{["Student ID", "Name", "Email", "Phone", "Applications", "Officer", "Nearest deadline", "Last activity"].map(h => <th key={h} className="text-left px-4 py-3">{h}</th>)}</tr>
          </thead>
          <tbody>
            {(rows || []).map(s => (
              <tr key={s.id} data-testid={`b2b-student-row-${s.id}`} onClick={() => nav(`/students/${s.id}`)} className="border-t border-slate-100 hover:bg-orange-50/40 cursor-pointer">
                <td className="px-4 py-3 mono text-xs">{s.student_code}</td>
                <td className="px-4 py-3 font-medium text-slate-900">{s.student_name}</td>
                <td className="px-4 py-3 text-slate-600">{s.email || "—"}</td>
                <td className="px-4 py-3 text-slate-600">{s.phone || "—"}</td>
                <td className="px-4 py-3">{s.app_counts.total}{s.app_counts.active ? <span className="text-xs text-slate-500"> · {s.app_counts.active} open</span> : null}</td>
                <td className="px-4 py-3 text-slate-700">{s.responsible_officer || "—"}</td>
                <td className="px-4 py-3 text-xs">{s.nearest_deadline ? <><b>{fmtDay(s.nearest_deadline.date)}</b><div className="text-slate-500">{s.nearest_deadline.kind} · {(d => d < 0 ? `${-d} day${d === -1 ? "" : "s"} overdue` : d === 0 ? "today" : `${d} day${d === 1 ? "" : "s"} left`)(daysUntil(s.nearest_deadline.date))}</div></> : "Not set"}</td>
                <td className="px-4 py-3 text-slate-500">{fmtDate(s.last_activity)}</td>
              </tr>
            ))}
            {rows === null && <tr><td colSpan={8} data-testid="b2b-students-loading" className="px-4 py-10 text-center text-slate-500">Loading students…</td></tr>}
            {rows !== null && rows.length === 0 && <tr><td colSpan={8} className="px-4 py-10 text-center text-slate-500">No students found</td></tr>}
          </tbody>
        </table>
      </div>
      <NewStudentDialog open={open} onOpenChange={setOpen} onCreated={s => nav(`/students/${s.id}`)} />
    </div>
  );
}

export function NewStudentDialog({ open, onOpenChange, onCreated }) {
  const [form, setForm] = useState({});
  const [partners, setPartners] = useState([]);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  useEffect(() => { if (open) { api.get("/meta").then(r => setPartners(r.data.partners || [])); } }, [open]);

  const submit = async () => {
    if (!form.student_name) return toast.error("Student name is required");
    if (!form.partner_id) return toast.error("Please select the partner (immigration company)");
    try {
      const r = await api.post("/b2b/students", form);
      toast.success(`Student ${r.data.student_code} created`); setForm({}); onOpenChange(false); onCreated?.(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to create student"); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="new-student-dialog" className="max-w-2xl bg-white">
        <DialogHeader><DialogTitle className="heading-font">New Student</DialogTitle></DialogHeader>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <F label="Full Name *"><Input data-testid="new-student-name" value={form.student_name || ""} onChange={e => set("student_name", e.target.value)} /></F>
          <F label="Email"><Input data-testid="new-student-email" value={form.email || ""} onChange={e => set("email", e.target.value)} /></F>
          <F label="Phone"><Input data-testid="new-student-phone" value={form.phone || ""} onChange={e => set("phone", e.target.value)} /></F>
          <F label="Date of Birth"><Input data-testid="new-student-dob" type="date" value={form.date_of_birth || ""} onChange={e => set("date_of_birth", e.target.value)} /></F>
          <F label="Location">
            <Select value={form.student_location || ""} onValueChange={v => set("student_location", v)}>
              <SelectTrigger data-testid="new-student-location"><SelectValue placeholder="Onshore / Offshore" /></SelectTrigger>
              <SelectContent>{["Onshore", "Offshore"].map(x => <SelectItem key={x} value={x}>{x}</SelectItem>)}</SelectContent>
            </Select>
          </F>
          <F label="Partner *">
            <Select value={form.partner_id || ""} onValueChange={v => set("partner_id", v)}>
              <SelectTrigger data-testid="new-student-partner"><SelectValue placeholder="Select partner" /></SelectTrigger>
              <SelectContent>{partners.map(p => <SelectItem key={p.id} value={p.id}>{p.company_name}</SelectItem>)}</SelectContent>
            </Select>
          </F>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button data-testid="new-student-submit" onClick={submit} className="bg-orange-500 hover:bg-orange-600 text-white">Create Student</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const F = ({ label, children }) => <div className="space-y-1.5"><Label className="text-xs text-slate-600">{label}</Label>{children}</div>;
